import React, { useEffect, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import {
	useTheme,
	ListItemText,
	useMediaQuery,
	TableHead,
	TableRow,
	TableCell,
	Grid,
	IconButton,
	Tooltip,
	LinearProgress,
	CircularProgress,
} from '@mui/material';

import LocalGroceryStoreIcon from '@mui/icons-material/LocalGroceryStore';
import SearchToolbar from 'components/SearchToolbar';
import ResponsiveTable from 'components/ResponsiveTable';
import { CustomTooltipProps } from 'components/tooltips/CustomTooltip/CustomTooltip';
import DateFilters from 'components/SoldProductsFilters/DateFilters';
import OtherFilters from 'components/SoldProductsFilters/OtherFilters';
import DownloadIcon from '@mui/icons-material/Download';
import RefreshIcon from '@mui/icons-material/Refresh';
import { sanitizedInput } from 'lib/helpers';
import useListSoldProductsByVendor from 'hooks/useListSoldProductsByVendor';
import moment from 'moment';
import { FetchSoldProductsSearchProps } from 'lib/models/listSoldProducts';
import useSoldSummary from 'hooks/useSoldSummary';
import SoldSummaryBlock from 'components/SoldSummary';
import { SoldProductsFiltersState, FilterAction } from 'hooks/useSoldProductsFilters';
import { defaultSoldProductsRowsPerPage, soldProductsRowsPerPageOptions } from 'config/constants';

type Props = {
	editUrl?: string;
	paper?: boolean;
	disabled?: boolean;
	customAction?: CustomTooltipProps;
	filters: SoldProductsFiltersState;
	dispatch: React.Dispatch<FilterAction>;
};

export default function ListSoldProductsTable(props: Props) {
	const { filters, dispatch } = props;
	const {
		from,
		to,
		confirmationFrom,
		confirmationTo,
		cancellationFrom,
		cancellationTo,
		reservationFrom,
		reservationTo,
		selectedStatuses,
		selectedVendors,
		selectedSections,
		isInAppSale,
		inAppSaleType,
		reservationType,
	} = filters;

	const { t } = useTranslation();

	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down('md'));

	const [search, setSearch] = useState('');
	const [page, setPage] = React.useState(0);
	const [rowsPerPage, setRowsPerPage] = React.useState(defaultSoldProductsRowsPerPage);

	// Build filter props for API calls and summary
	const filterProps: FetchSoldProductsSearchProps = useMemo(() => {
		const props: FetchSoldProductsSearchProps = {};

		if (search) {
			props.search = search;
		}
		if (from) {
			props.from = from.toISOString();
		}
		if (to) {
			props.to = to.toISOString();
		}
		if (confirmationFrom) {
			props.confirmationFrom = confirmationFrom.toISOString();
		}
		if (confirmationTo) {
			props.confirmationTo = confirmationTo.toISOString();
		}
		if (cancellationFrom) {
			props.cancellationFrom = cancellationFrom.toISOString();
		}
		if (cancellationTo) {
			props.cancellationTo = cancellationTo.toISOString();
		}
		if (reservationFrom) {
			props.reservationFrom = reservationFrom.toISOString();
		}
		if (reservationTo) {
			props.reservationTo = reservationTo.toISOString();
		}
		if (selectedStatuses.length > 0) {
			props.statuses = selectedStatuses;
		}
		if (selectedVendors.length > 0) {
			props.vendors = selectedVendors;
		}
		if (selectedSections.length > 0) {
			props.sections = selectedSections;
		}
		if (isInAppSale !== null) {
			props.isInAppSale = isInAppSale;
		}
		if (inAppSaleType) {
			props.inAppSaleType = inAppSaleType;
		}
		if (reservationType) {
			props.reservationType = reservationType;
		}

		return props;
	}, [
		search,
		from,
		to,
		confirmationFrom,
		confirmationTo,
		cancellationFrom,
		cancellationTo,
		reservationFrom,
		reservationTo,
		selectedStatuses,
		selectedVendors,
		selectedSections,
		isInAppSale,
		inAppSaleType,
		reservationType,
	]);

	const searchProps: FetchSoldProductsSearchProps = useMemo(
		() => ({
			...filterProps,
		}),
		[filterProps]
	);

	const { loading, soldProductsByVendor, refetch } = useListSoldProductsByVendor(searchProps);
	const { loading: summaryLoading, summary } = useSoldSummary(filterProps);
	const [exportingCsv, setExportingCsv] = useState(false);

	const handleChangePage = (_: unknown, newPage: number) => {
		setPage(newPage);
	};

	const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
		setRowsPerPage(parseInt(event.target.value, 10));
		setPage(0);
	};

	const searchValue = sanitizedInput(search);
	const allSoldProducts = soldProductsByVendor?.elements ?? [];

	const filteredSoldProducts = allSoldProducts.filter((soldProduct) => {
		const matchesSearch =
			!searchValue ||
			sanitizedInput(soldProduct.comboTitle).includes(searchValue) ||
			sanitizedInput(soldProduct.vendor).includes(searchValue);

		return matchesSearch;
	});

	const paginatedSoldProducts = isMobile
		? filteredSoldProducts
		: filteredSoldProducts.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

	const exportToCsv = () => {
		try {
			setExportingCsv(true);
			const headers = ['Usuario interno', 'Código Producto', 'Descripcion', 'Mixer', 'Cantidad Total'];
			const rows = filteredSoldProducts.map((product) => [
				product.vendor || '-',
				product.code || '-',
				product.comboTitle,
				product._id?.mixer || '-',
				product.totalQuantities,
			]);

			let csvContent = 'data:text/csv;charset=utf-8,';
			csvContent += headers.join(',') + '\n';
			rows.forEach((rowArray) => {
				csvContent += rowArray.join(',') + '\n';
			});

			const encodedUri = encodeURI(csvContent);
			const timestamp = moment().format('YYYY-MM-DD_HH-mm');
			const link = document.createElement('a');
			link.setAttribute('href', encodedUri);
			link.setAttribute('download', `listado_productos_vendidos_por_usuario_interno_${timestamp}.csv`);
			document.body.appendChild(link);
			link.click();
			document.body.removeChild(link);
		} catch (error) {
			console.error('Error exporting CSV:', error);
		} finally {
			setExportingCsv(false);
		}
	};

	useEffect(() => {
		setPage(0);
	}, [
		search,
		from,
		to,
		confirmationFrom,
		confirmationTo,
		cancellationFrom,
		cancellationTo,
		reservationFrom,
		reservationTo,
		selectedStatuses,
		selectedVendors,
		selectedSections,
		isInAppSale,
		inAppSaleType,
		reservationType,
	]);

	return (
		<>
			{!!loading && <LinearProgress />}
			<Grid container spacing={2} alignItems="center">
				<Grid item xs={8}>
					<SearchToolbar
						onSearch={(value) => setSearch(value)}
						live={true}
						disabled={props.disabled}
						customAction={props.customAction}
					/>
				</Grid>
				<Grid item xs={8}>
					<DateFilters filters={filters} dispatch={dispatch} />
				</Grid>
				<Grid item xs={8}>
					<OtherFilters filters={filters} dispatch={dispatch} />
				</Grid>
				<Grid item xs={10}>
					<SoldSummaryBlock summary={summary} loading={summaryLoading} />
				</Grid>
				<Grid item container xs={4}>
					<Grid item xs={1}>
						<Tooltip title={t('common:refresh') || 'Actualizar'}>
							<IconButton onClick={() => refetch()}>
								<RefreshIcon />
							</IconButton>
						</Tooltip>
					</Grid>
					<Grid item xs={1}>
						<Tooltip title={t('common:downloadCsv') || 'Descargar CSV'}>
							<IconButton onClick={exportToCsv} disabled={exportingCsv}>
								{exportingCsv ? <CircularProgress size={24} /> : <DownloadIcon />}
							</IconButton>
						</Tooltip>
					</Grid>
				</Grid>
			</Grid>
			{!props.disabled && (
				<ResponsiveTable
					elements={paginatedSoldProducts}
					list={{
						primaryKey: '_id',
						avatar: <LocalGroceryStoreIcon />,
						renderListItemText: (product) => (
							<ListItemText
								primary={`${product?.vendor || '-'}`}
								secondary={`
								${t('common:description')}: ${product.comboTitle} | ${t('common:mixer')}: ${product._id.mixer || '-'} |
								${t('common:quantity')}`}
							/>
						),
					}}
					table={{
						renderHead: () => (
							<TableHead>
								<TableRow>
									<TableCell align="left">Código Producto</TableCell>
									<TableCell align="left">{t('common:description')}</TableCell>
									<TableCell align="left">
										{t('common:quantity')} {t('common:total')}
									</TableCell>
									<TableCell align="left">{t('common:vendor')}</TableCell>
								</TableRow>
							</TableHead>
						),
						renderRow: (product) => (
							<TableRow tabIndex={-1} key={`${product.orderId}-${product.productId}-${product.comboTitle}`}>
								<TableCell>{product.code || '-'}</TableCell>
								<TableCell>{product.comboTitle}</TableCell>
								<TableCell>{product.totalQuantities}</TableCell>
								<TableCell>{product?.vendor || '-'}</TableCell>
							</TableRow>
						),
						pagination: props.disabled
							? null
							: {
									count: filteredSoldProducts.length,
									page,
									rowsPerPage,
									rowsPerPageOptions: soldProductsRowsPerPageOptions,
									onPageChange: handleChangePage,
									onRowsPerPageChange: handleChangeRowsPerPage,
							  },
					}}
				/>
			)}
		</>
	);
}
